// Module Federation async boundary (TPX-291).
//
// This entry does NOTHING except dynamically import `./bootstrap`. The dynamic
// import is deliberate: it defers the real app — and crucially every consumer
// of an MF shared singleton (`@exacteraai/platform-ui-auth`,
// `@exacteraai/platform-ui-services`) in bootstrap's import graph — into an
// async chunk that the bundler loads only AFTER the Module Federation runtime
// has initialized the shared scope. The plugin rewrites shared imports inside
// that async chunk to `await loadShare(...)`, so the singletons are guaranteed
// resolved before `initialize()`/`createClient()` run.
//
// Without this boundary the shared imports sit in the synchronous entry and
// race the share-scope init: `createClient`/`initialize` is intermittently
// `undefined` at the top-level call, throwing "<x> is not a function" and
// white-screening the app. The race timing shifts with @module-federation/vite
// version, build platform, and CPU/network, so it manifests unevenly (green on
// one machine, failing in CI) — see TPX-291. Keep this file a pure
// dynamic-import shim; do not add static imports of app or shared modules here
// (that would re-introduce the eager shared-singleton consumption this boundary
// exists to defer) — only dependency-free browser APIs.

// Vite emits `vite:preloadError` when a dynamically imported chunk fails to
// load — most often a still-open tab whose no-cache index.html now points at a
// chunk hash that 404s right after a deploy. A full reload re-fetches the
// (no-cache) index.html and its current hashes. This is Vite's documented
// recovery and pairs with the `Cache-Control: no-cache` we set on index.html.
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

// A failed bootstrap load must not leave a silent unhandled rejection behind a
// permanently blank #root. RUM isn't initialized yet (that happens inside
// bootstrap), so this console.error is the only signal; render a
// dependency-free, CSP-safe fallback (no inline handlers) with a reload action
// rather than a blank page. `vite:preloadError` above auto-recovers the stale-
// chunk case; this covers genuine module-evaluation failures, where blindly
// reloading would loop.
import('./bootstrap').catch((err: unknown) => {
  console.error('[bootstrap] failed to load', err);
  const root = document.getElementById('root');
  if (!root) return;
  root.replaceChildren();
  const wrap = document.createElement('div');
  wrap.setAttribute(
    'style',
    'font-family:Inter,system-ui,sans-serif;max-width:32rem;margin:4rem auto;padding:0 1rem;text-align:center',
  );
  const heading = document.createElement('h1');
  heading.textContent = 'Something went wrong';
  const message = document.createElement('p');
  message.textContent = 'The application failed to load. Please reload the page.';
  const reload = document.createElement('button');
  reload.textContent = 'Reload';
  reload.setAttribute('style', 'margin-top:1rem;padding:0.5rem 1rem;cursor:pointer');
  reload.addEventListener('click', () => window.location.reload());
  wrap.append(heading, message, reload);
  root.appendChild(wrap);
});
